import { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Paper,
  Divider,
  Typography,
} from '@mui/material';
import { radius } from '../../../../../theme';
import CabecalhoNota from '../cabecalhoNota';
import { map } from 'lodash';
import { useUsuario } from '../../../../../providers/usuarioProvider';
import { useSnackBar } from '../../../../../stores/snackBarStores';
import FelixAnalytics from '../../../../../felixAnalytics';
import { IconArrowLeft, IconFileUpload, IconTrashX } from '@tabler/icons-react';
import { NotaBMF } from '../../../../../models/nota';
import { OperacaoLeitorBMF } from '../../../../../models/operacao';
import Operacoes from '../operacoes';
import CustosBmf from './custosBmf';
import ResumoNegocios from './resumoNegocios';
import CustosCorretagem from './custosCorretagem';
import { deleteDoc, onSnapshot, query, where } from 'firebase/firestore';
import OperacaoDb from '../../../../../db/operacaoDb';
import NotaDb from '../../../../../db/notaDb';
import { useNavigate } from 'react-router-dom';

type Props = { nota: NotaBMF };

export default function Bmf({ nota }: Props) {
  const navigate = useNavigate();
  const snackBar = useSnackBar();
  const { visualizado } = useUsuario();

  const [operacoes, setOperacoes] = useState<OperacaoLeitorBMF[]>([]);

  useEffect(() => {
    return onSnapshot(
      query(
        new OperacaoDb().collection({
          usuarioId: nota.usuario,
          operacaoId: '',
        }),
        where('nota', '==', nota.id),
      ),
      (operacoesSnapshot) => {
        setOperacoes(
          map(operacoesSnapshot.docs, (doc) => doc.data() as OperacaoLeitorBMF),
        );
      },
    );
  }, [nota]);

  const handleClose = () => {
    setAbrirPopupExclusao(false);
  };
  const handleOpen = () => {
    setAbrirPopupExclusao(true);
  };
  const [abrirPopupExclusao, setAbrirPopupExclusao] = useState(false);
  const apagarNota = () => {
    if (!!visualizado) {
      deleteDoc(
        new NotaDb().doc({ usuarioId: visualizado, notaId: nota.id }),
      ).then(() => {
        snackBar({
          type: 'ALERT',
          payload: {
            mensagem: 'Já apaguei aquela nota, viu?',
            severidade: 'success',
          },
        });
        FelixAnalytics.identify(visualizado);
        FelixAnalytics.track('nota-apagada', { nota: nota.id });
      });
      navigate(-1);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-start"
        pt={4}
        pb={2}
      >
        <Box flexGrow={1} alignSelf="center">
          <Typography variant="eyebrow" color="text.secondary">
            Nota de corretagem — BM&F
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate(-1)}
          startIcon={<IconArrowLeft />}
        >
          Voltar
        </Button>
        <Box m={1 / 2} />

        {nota.upload && (
          <>
            <Button
              variant="outlined"
              color="info"
              onClick={() => navigate(`/arquivo/upload/${nota.upload}`)}
              startIcon={<IconFileUpload />}
            >
              Ver upload
            </Button>
            <Box m={1 / 2} />
          </>
        )}

        <Button
          variant="outlined"
          color="error"
          onClick={handleOpen}
          startIcon={<IconTrashX />}
        >
          Apagar
        </Button>
        <Dialog
          open={abrirPopupExclusao}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            Quer mesmo apagar essa nota?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Não será possível desfazer essa operação.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="inherit">
              Cancelar
            </Button>
            <Button onClick={apagarNota} variant="contained" color="error">
              Apagar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      <Box pb={4}>
        {/* Painel grande: radius xl + hairline, sem sombra (DESIGN.md → Cards). */}
        <Paper variant="outlined" sx={{ borderRadius: `${radius.xl}px` }}>
          <Box paddingX={1} paddingY={2}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12}>
                <CabecalhoNota
                  corretora={nota.corretora}
                  dataPregao={nota.dataPregao}
                  numeroNota={nota.numeroNota}
                  readOnly={true}
                />
              </Grid>
              <Grid item xs={12}>
                <Box paddingX={1} paddingY={1}>
                  <Operacoes operacoes={operacoes} readOnly={true} />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider orientation="horizontal" variant="fullWidth" />
              </Grid>
              <Grid item xs={4}>
                <CustosBmf
                  taxaOperacional={nota.taxaOperacional}
                  taxaRegistro={nota.taxaRegistro}
                  taxasBmf={nota.taxasBmf}
                />
              </Grid>
              <Grid item xs={4}>
                <CustosCorretagem
                  irrf={nota.irrf}
                  irrfDaytrade={nota.irrfDaytrade}
                  iss={nota.iss}
                />
              </Grid>
              <Grid item xs={4}>
                <ResumoNegocios
                  outros={nota.outros}
                  outrosCustos={nota.outrosCustos}
                  valorLiquido={nota.valorLiquido}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
